import React, {
    useCallback, useEffect, useMemo, useState, useRef,
} from 'react';
import {
    RadioChangeEvent,
    Popconfirm,
    message,
    Alert,
    notification,
    Typography
} from '@revfluence/fresh';
import {
    ChevronRightIcon,
} from '@revfluence/fresh-icons/regular/esm';
import { Tooltip, TooltipContent, TooltipTrigger } from '@frontend/shadcn/components/ui/tooltip';

import {
    filter, first, flatten, get, groupBy, has, isEmpty, isNull, keys, map,
    size,
} from 'lodash';
import { useMutation } from '@apollo/client';
import { Sheet, SheetContent, SheetFooter, SheetHeader, SheetTitle } from '@frontend/shadcn/components/ui/sheet';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faCalendar, faCirclePlay, faFileInvoiceDollar, faLink, faTag, faTags } from '@fortawesome/free-solid-svg-icons';
import { Button } from '@frontend/shadcn/components/ui/button';
import { Input } from '@frontend/shadcn/components/ui/input';
import { useClientFeatures } from '@frontend/context/ClientFeatureContext';
import {
    OFFER_SOURCE, UpdateAdditionalDeeplinkBulk, UpdateAffiliateOfferDeeplinkBulkInput, UpdateAffiliatePayoutInput,
} from '../../types/globalTypes';
import { useUpdateOfferMembers } from '../../hooks';
import {
    IAdditionalDeeplinkGroupedMember, IAffiliateDeeplinkInfo, IManageDeeplinkForm, TMember, TMode,
} from '../MembersWizard/types';
import {
    GetOfferById_offer, GetOfferById_offer_links_affiliates, GetOfferById_offer_promos_affiliates,
} from '../../queries/types/GetOfferById';
import { OfferDetailCard } from '../AffiliatesApp/components/OfferDetailCard';
import { cleanPromoCode } from '../MembersWizard/utils/cleanPromoCode';
import ManageDeepLink from './Sections/ManagePimaryDeepLink/ManageDeepLink';
import { REMOVE_BULK_DEEPLINKS } from '../../queries/removeBulkDeeplinksMutation';
import { RefreshManageOfferItem } from './RefreshManageOfferItem';
import RefreshManageOfferPayout from './Sections/RefreshSection/RefreshManageOfferPayout';
import RefreshPromoCodes from './Sections/RefreshSection/RefreshPromoCodes';
import RefreshManageOfferActivation from './Sections/RefreshSection/RefreshManageOfferActivation';
import RefreshOfferBulkDateAction from './Sections/RefreshSection/RefreshOfferBulkDateAction';
import { AUTOMATED_PAYOUT_ID, AutomatedPayoutLabel } from '../../constants/offers';

const { Text } = Typography;

interface IProps {
    offer: GetOfferById_offer;
    visible: boolean;
    onComplete: () => void;
    selectedMembers?: readonly TMember[];
    offerSource?: OFFER_SOURCE;
    onClose?: () => void;
    selectedMemberIds?: number[];
    isWorkFlow?: boolean;
    setIsVisibleSelectOffer?: (value: boolean) => void
    manageDeepLinksAllowed?: boolean
}
enum AFFILIATE_STATUS {
    ACTIVE = 'active',
    DELETED = 'deleted',
    SUCCESS = 'success',
}
export interface CustomColumn {
    id: string;
    accessorKey: string;
    header: string;
    size: number;
    minSize: number;
    maxSize: number;
    cell?: (row) => React.ReactNode;
}

enum OFFER_ACTION_TYPE {
    ACTIVATE = 'activate',
    DEACTIVATE = 'deactivate',
    REFRESH = 'refresh',
    EDITPAYOUT = 'editPayout',
    UPDATE_BULK_ACTIVE_DATES = 'updateBulkActiveDates',
    UPDATE_BULK_REFRESH_DATES = 'refreshDates',
    DEEPLINK = 'deepLink',
}
type ActiveDate = {
    startDate: Date,
    endDate: Date,
    showEndDate: boolean
};

// Extracted helper to transform member data for payout and activation/deactivation
function transformMemberData(members: TMember[], offerSource: OFFER_SOURCE, offer: GetOfferById_offer): Array<{
    key: number;
    name: string;
    code: string;
    payoutId: number | undefined;
    status: string;
    affiliateLink: string;
}> {
    return members.map((member, memberIndex) => {
        const matchingAffiliate = offerSource === OFFER_SOURCE.SHOPIFY
            ? offer.promos[0].affiliates.find((affiliate) => affiliate.id === member.affiliateOfferId)
            : offerSource === OFFER_SOURCE.TUNE
                ? offer.links[0].affiliates.find((affiliate) => affiliate.affiliateId === member.affiliateId)
                : null;
        const affiliateLink = offerSource === OFFER_SOURCE.TUNE ? offer.links[0].affiliateStats.find((affiliate) => affiliate.affiliateId === member.affiliateId) : {};

        let affiliateStatus: string = '';
        if (offerSource === OFFER_SOURCE.SHOPIFY) {
            const matchingAffiliateStats = offer.promos[0].affiliates.find(
                (affiliate) => affiliate.id === member.affiliateOfferId,
            ) as GetOfferById_offer_promos_affiliates | undefined;
            if (matchingAffiliateStats) {
                affiliateStatus = matchingAffiliateStats.status.toLowerCase() === 'success' ? 'Active' : 'Inactive';
            }
        } else {
            const matchingAffiliateStats = offer.links[0].affiliateStats.find((affiliate) => affiliate.affiliateId === member.affiliateId);
            affiliateStatus = matchingAffiliateStats?.linkStatus === 'Active' ? 'Active' : 'Inactive';
        }
        return {
            key: memberIndex,
            name: member.name,
            code: offerSource === OFFER_SOURCE.SHOPIFY && matchingAffiliate && 'externalDiscountCode' in matchingAffiliate
                ? (matchingAffiliate as GetOfferById_offer_promos_affiliates).externalDiscountCode
                : '',
            payoutId: matchingAffiliate?.offerPayoutId,
            status: affiliateStatus,
            affiliateLink: offerSource === OFFER_SOURCE.TUNE && affiliateLink && 'affiliateShortLink' in affiliateLink
                ? (affiliateLink as { affiliateShortLink?: string }).affiliateShortLink || ''
                : '',
        };
    });
}

export const RefreshManageOffer: React.FC<Readonly<IProps>> = React.memo((props) => {
    const {
        offer,
        visible,
        offerSource,
        onComplete,
        selectedMembers,
        onClose,
        selectedMemberIds,
        isWorkFlow,
        setIsVisibleSelectOffer,
        manageDeepLinksAllowed = false,
    } = props;
    const isAutomatedPayoutEnabled = offer?.offerAutomationConfig?.isActive || false;
    const { automatePayoutTier } = useClientFeatures();
    const defaultPayout = offerSource === OFFER_SOURCE.SHOPIFY ? offer.promos[0].defaultPayoutId : offer.links[0].defaultPayoutId;
    const isUngrouped = offerSource === OFFER_SOURCE.SHOPIFY ? offer.promos[0].isUngrouped : false;
    const [activeDates, setActiveDates] = useState<ActiveDate>({
        startDate: new Date(),
        endDate: null,
        showEndDate: false,
    });
    const [removeDeeplinks] = useMutation(REMOVE_BULK_DEEPLINKS);
    const [removeDisabled, setRemoveDisabled] = useState(true);
    const [mode, setMode] = useState<string | null>(null);
    const [data, setData] = useState<TMember[]>(selectedMembers as TMember[]);
    const [defaultPayoutId, setDefaultPayoutId] = useState<number | null>(
        automatePayoutTier && isAutomatedPayoutEnabled ? AUTOMATED_PAYOUT_ID : defaultPayout
    );
    const [isError, setIsError] = useState<boolean>(false);
    const [isDuplicateError, setIsDuplicateError] = useState<boolean>(false);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [selectedOfferMember, setSelectedOfferMember] = useState<(TMember & { status: string })[]>([]);
    const [toActivateMembers, setToActivateMember] = useState<(TMember & { status: string })[]>([]);
    const [toDeactivateMembers, setToDeactivateMember] = useState<(TMember & { status: string })[]>([]);
    const memberToOperate = mode === OFFER_ACTION_TYPE.ACTIVATE ? toActivateMembers : toDeactivateMembers;
    const netMembers = selectedMemberIds?.length - selectedMembers?.length;
    const [activeOrDeactive, setActiveOrDeactive] = useState('');
    const [selectedLabels, setSelectedLabels] = useState([]);
    const [showDeeplinkRemoveTooltip, setShowDeeplinkRemoveTooltip] = useState(false);

    const [selectedActiveMembers, setSelectedActiveMembers] = useState([]);
    const [_, setDefaultDeeplinkUrl] = useState('');
    const [additionalDeeplinkGroupedMembers, setAdditionalDeeplinkGroupedMembers] = useState<IAdditionalDeeplinkGroupedMember>(null);
    const [manageDeeplinkForm, setManageDeeplinkForm] = useState<IManageDeeplinkForm>(null);
    const [linkValidation, setLinkValidation] = useState(false);
    // ---end Manage Deeplink states
    const [isFormViewVisible, setFormViewVisible] = useState(false);
    const inputRefs = useRef<{ [key: string]: HTMLInputElement | null }>({});

    useEffect(() => {
        setSelectedActiveMembers(
            filter(selectedOfferMember, (member) => member.status.toLowerCase() === AFFILIATE_STATUS.ACTIVE),
        );
    }, [selectedOfferMember]);

    useEffect(() => {
        if (!selectedActiveMembers.length) {
            setAdditionalDeeplinkGroupedMembers(null);
        } else {
            const flattenedAdditionalLinks = flatten(map(selectedActiveMembers, (member): IAffiliateDeeplinkInfo[] => {
                const additionalDeeplinks = member.additionalDeeplinks || [];
                return map(
                    additionalDeeplinks,
                    (deeplink): IAffiliateDeeplinkInfo => ({
                        affiliateDeeplinkId: deeplink.id,
                        memberId: member.id,
                        memberName: member.name,
                        label: deeplink.label,
                    }),
                );
            }));
            const groupedByLabel: IAdditionalDeeplinkGroupedMember = groupBy(flattenedAdditionalLinks, 'label');
            setAdditionalDeeplinkGroupedMembers(groupedByLabel);
        }
    }, [selectedActiveMembers]);

    useEffect(() => {
        const result = selectedMembers.map((selectedMember) => {
            let offerAffiliate = null;
            if (offerSource === OFFER_SOURCE.SHOPIFY) {
                const offerAffiliates = offer.promos[0].affiliates;
                offerAffiliate = offerAffiliates.find(
                    (offerAffiliate) => offerAffiliate.id === selectedMember.affiliateOfferId
                        && offerAffiliate.affiliate.status.toLowerCase() !== AFFILIATE_STATUS.DELETED,
                ) as GetOfferById_offer_promos_affiliates | undefined;
                return {
                    ...selectedMember,
                    status: offerAffiliate?.status ?? null,
                };
            } else {
                const offerAffiliates = offer.links[0].affiliates;
                const offerAffiliateStats = offer.links[0].affiliateStats;
                offerAffiliate = offerAffiliates.find(
                    (offerAffiliate) => offerAffiliate.affiliateId === selectedMember.affiliateId
                        && offerAffiliate.affiliate.status.toLowerCase() !== AFFILIATE_STATUS.DELETED,
                ) as GetOfferById_offer_links_affiliates | undefined;
                return {
                    ...selectedMember,
                    status: (offerAffiliateStats.find((affStat) => affStat.affiliateOfferId === offerAffiliate?.id)?.linkStatus || undefined) ?? null,
                };
            }
        });
        const newResult = result.filter((res) => res.status !== null);
        setData(newResult);
        setSelectedOfferMember(newResult);
    }, [selectedMembers, offer, offerSource]);

    const handleSteps = (mode: TMode) => {
        setIsLoading(false);
        if (mode === OFFER_ACTION_TYPE.DEACTIVATE || mode === OFFER_ACTION_TYPE.REFRESH) {
            setIsError(true);
        } else if (mode === OFFER_ACTION_TYPE.EDITPAYOUT) {
            setIsError(false);
        }
        setMode(mode);
    };
    const makeEmptyStates = useCallback(() => {
        setToActivateMember([]);
        setToDeactivateMember([]);
        setActiveOrDeactive('');
        setActiveDates({
            startDate: new Date(),
            endDate: null,
            showEndDate: false,
        });
        setSelectedLabels([]);
    }, [setToActivateMember, setToDeactivateMember, setActiveOrDeactive, setActiveDates]);
    const goBack = () => {
        makeEmptyStates();
        if (isWorkFlow && mode === null) {
            onClose();
            setIsVisibleSelectOffer(true);
        } else if (mode === null) {
            onClose();
        } else {
            if (mode === OFFER_ACTION_TYPE.UPDATE_BULK_ACTIVE_DATES) {
                setMode(OFFER_ACTION_TYPE.REFRESH);
            } else {
                setMode(null);
                handleClearInput();
            }
        }
    };
    let hookMode: TMode = OFFER_ACTION_TYPE.DEACTIVATE;
    if (mode === OFFER_ACTION_TYPE.ACTIVATE && offerSource === OFFER_SOURCE.TUNE) {
        hookMode = OFFER_ACTION_TYPE.REFRESH;
    } else if (mode === OFFER_ACTION_TYPE.DEACTIVATE) {
        hookMode = OFFER_ACTION_TYPE.DEACTIVATE;
    } else if (mode === OFFER_ACTION_TYPE.REFRESH || mode === OFFER_ACTION_TYPE.UPDATE_BULK_ACTIVE_DATES || mode === OFFER_ACTION_TYPE.UPDATE_BULK_REFRESH_DATES) {
        hookMode = OFFER_ACTION_TYPE.REFRESH;
    } else if (mode === OFFER_ACTION_TYPE.EDITPAYOUT) {
        hookMode = OFFER_ACTION_TYPE.EDITPAYOUT;
    } else if (mode === OFFER_ACTION_TYPE.ACTIVATE && offerSource === OFFER_SOURCE.SHOPIFY) {
        hookMode = OFFER_ACTION_TYPE.REFRESH;
    } else if (mode === OFFER_ACTION_TYPE.DEEPLINK) {
        hookMode = OFFER_ACTION_TYPE.DEEPLINK;
    }

    const { onSave } = useUpdateOfferMembers(
        offer.id,
        hookMode,
        offerSource,
        onComplete,
        offerSource === OFFER_SOURCE.SHOPIFY ? offer.promos[0].defaultPayoutId : offer.links[0].defaultPayoutId,
    );
    let manageOfferVariable = null;
    switch (offerSource) {
        case OFFER_SOURCE.SHOPIFY:
            if (mode === OFFER_ACTION_TYPE.DEACTIVATE) {
                const extractedIdsArray = toDeactivateMembers.map((item) => ({ id: item.affiliateOfferId }));
                manageOfferVariable = {
                    affiliates: extractedIdsArray,
                    id: offer.id,
                };
            } else if (mode === OFFER_ACTION_TYPE.ACTIVATE) {
                const affiliates = toActivateMembers.map((affiliate) => ({
                    memberId: affiliate.id,
                    desiredPromoCode: affiliate.previousCode.code,
                }));
                manageOfferVariable = {
                    affiliates,
                    endDate: null,
                    startDate: null,
                    id: offer.id,
                };
            } else if (mode === OFFER_ACTION_TYPE.REFRESH || mode === OFFER_ACTION_TYPE.UPDATE_BULK_ACTIVE_DATES || mode === OFFER_ACTION_TYPE.UPDATE_BULK_REFRESH_DATES) {
                const affiliates = data.map((affiliate) => ({
                    memberId: affiliate.id,
                    desiredPromoCode: mode === OFFER_ACTION_TYPE.UPDATE_BULK_REFRESH_DATES ? affiliate.previousCode.code : affiliate.newCode,
                    ...(isUngrouped ? { startDate: activeDates.startDate, endDate: activeDates.endDate } : {}),
                }));
                manageOfferVariable = {
                    affiliates,
                    endDate: null,
                    startDate: null,
                    id: offer.id,
                };
            } else if (mode === OFFER_ACTION_TYPE.EDITPAYOUT) {
                manageOfferVariable = data.map((member) => ({
                    id: member.affiliateOfferId,
                    oldPayoutId: member.payoutId,
                    newPayoutId: defaultPayoutId === AUTOMATED_PAYOUT_ID ? null : defaultPayoutId,
                    isAutomatedPayoutAffiliate: defaultPayoutId === AUTOMATED_PAYOUT_ID
                }));
                manageOfferVariable = manageOfferVariable as UpdateAffiliatePayoutInput[];
            }
            break;
        case OFFER_SOURCE.TUNE:
            if (mode === OFFER_ACTION_TYPE.EDITPAYOUT) {
                manageOfferVariable = data.map((member) => ({
                    id: member.affiliateOfferId,
                    oldPayoutId: member.payoutId,
                    newPayoutId: defaultPayoutId === AUTOMATED_PAYOUT_ID ? null : defaultPayoutId,
                    isAutomatedPayoutAffiliate: defaultPayoutId === AUTOMATED_PAYOUT_ID
                }));
                manageOfferVariable = manageOfferVariable as UpdateAffiliatePayoutInput[];
            } else if (mode === OFFER_ACTION_TYPE.ACTIVATE || mode === OFFER_ACTION_TYPE.DEACTIVATE) {
                // Activate and deactivate link offer
                const ids = data.map((item) => item.affiliateOfferId);
                if (ids.length) {
                    manageOfferVariable = {
                        ids,
                    };
                }
            }
            break;
        default:
    }
    const [deeplinkData, setDeeplinkData] = useState(null);
    useEffect(() => {
        if (mode === OFFER_ACTION_TYPE.DEEPLINK) {
            if (isFormViewVisible) {
                const ids = selectedActiveMembers.map((item) => item.affiliateOfferId);

                let defaultDeeplink: UpdateAffiliateOfferDeeplinkBulkInput;
                const additionalDeeplinks: UpdateAdditionalDeeplinkBulk[] = [];
                if (
                    has(manageDeeplinkForm, 'Primary Link')
                    && !isEmpty(get(manageDeeplinkForm, 'Primary Link').url)
                ) {
                    defaultDeeplink = {
                        affiliateOfferLinkIds: ids,
                        deepLinkUrl: get(manageDeeplinkForm, 'Primary Link').url,
                    };
                }
                map(manageDeeplinkForm, (val, label) => {
                    if (label !== 'Primary Link') {
                        const affiliateDeeplinkIds = map(additionalDeeplinkGroupedMembers[label] || [], 'affiliateDeeplinkId');
                        const newLabel = !isEmpty(val.label) ? val.label : null;
                        const url = !isEmpty(val.url) ? val.url : null;
                        additionalDeeplinks.push({
                            affiliateDeeplinkIds,
                            newLabel,
                            label,
                            url,
                        });
                    }
                });
                setDeeplinkData({
                    data: {
                        ...(defaultDeeplink ? { defaultDeeplink } : {}),
                        ...(additionalDeeplinks.length ? { additionalDeeplinks } : {}),
                    },
                });
            }
        }
    }, [mode, isFormViewVisible, manageDeeplinkForm, additionalDeeplinkGroupedMembers, selectedActiveMembers]);

    useEffect(() => {
        if (manageDeeplinkForm) {
            if (!manageDeeplinkForm['Primary Link'] && !isEmpty(keys(manageDeeplinkForm))) {
                setShowDeeplinkRemoveTooltip(false);
                setRemoveDisabled(false);
            } else {
                setRemoveDisabled(true);
                if (size(keys(manageDeeplinkForm)) > 1) {
                    setShowDeeplinkRemoveTooltip(true);
                } else {
                    setShowDeeplinkRemoveTooltip(false);
                }
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [keys(manageDeeplinkForm || {}).length]);

    const handleRemoveDeeplinks = async () => {
        const resultToRemove = [];
        map(manageDeeplinkForm, (_, label) => {
            const affiliateDeeplinkIdsForLable = map(additionalDeeplinkGroupedMembers[label], 'affiliateDeeplinkId');
            resultToRemove.push(...affiliateDeeplinkIdsForLable);
        });
        await removeDeeplinks({
            variables: {
                affiliateDeeplinkIds: resultToRemove,
            },
            onCompleted() {
                onComplete();
            },
        });
    };
    const showSuccessNotification = () => {
        notification.success({
            message: `Offer updated successfully for ${selectedMembers.length} member${selectedMembers.length > 1 ? 's' : ''}.`,
        });
    };
    const showErrorNotification = (msg = 'An error occurred') => {
        notification.error({
            message: msg,
        });
    };
    const save = () => {
        setIsLoading(true);
        if (isWorkFlow) {
            message.info('Offer update in progress. We will notify you within the platform once completed.');
        }
        // Check for empty fields in the data
        const hasEmptyField = data.some((item) => !item.newCode || item.newCode.trim() === '');

        if (mode === OFFER_ACTION_TYPE.REFRESH && hasEmptyField) {
            setIsError(true); // Set isError state to true if there's an empty field
        } else {
            setIsError(false);
            onSave(deeplinkData || manageOfferVariable, [])
                .then(() => {
                    setMode(null);
                    setIsLoading(false);
                    if (isWorkFlow) {
                        showSuccessNotification();
                    }
                    makeEmptyStates();
                    onClose();
                })
                .catch((err) => {
                    // Handle error
                    message.error(err.message);
                    if (isWorkFlow) {
                        showErrorNotification(err.message);
                    }
                    setMode(null);
                    makeEmptyStates();
                    onClose();
                });
        }
    };
    const ops = {
        [OFFER_ACTION_TYPE.ACTIVATE]: {
            filter: (member) =>
            (offerSource === OFFER_SOURCE.SHOPIFY
                ? member.status.toLowerCase() !== AFFILIATE_STATUS.SUCCESS
                : member.status.toLowerCase() !== AFFILIATE_STATUS.ACTIVE),
            set: setToActivateMember,
        },
        [OFFER_ACTION_TYPE.DEACTIVATE]: {
            filter: (member) =>
            (offerSource === OFFER_SOURCE.SHOPIFY
                ? member.status.toLowerCase() === AFFILIATE_STATUS.SUCCESS
                : member.status.toLowerCase() === AFFILIATE_STATUS.ACTIVE),
            set: setToDeactivateMember,
        },
    };
    const onChange = (e: RadioChangeEvent) => {
        setIsError(false);
        setMode(e.target.value);
        setActiveOrDeactive(e.target.value);

        const op = ops[e.target.value];
        if (op) {
            const filtered = selectedOfferMember.filter(op.filter);
            op.set(filtered);
            if (filtered.length <= 0) setIsError(true);
        }
    };
    const columns: CustomColumn[] = [
        {
            header: 'Member',
            id: 'name',
            accessorKey: 'name',
            size: 170,
            minSize: 170,
            maxSize: 170,
            cell: ({ row }) => (
              <Tooltip>
                <TooltipTrigger>
                  {row.original.name.length > 20 ? `${row.original.name.substring(0, 20)}...` : row.original.name}
                </TooltipTrigger>
                <TooltipContent className="bg-grey-0 text-grey-6 shadow-lg border border-gray-200 rounded-md px-3 py-2 font-medium z-50 max-w-[400px]">
                  {row.original.name}
                </TooltipContent>
              </Tooltip>
            ),
        },
        {
            header: 'Current status',
            id: 'status',
            accessorKey: 'status',
            size: 170,
            minSize: 170,
            maxSize: 170,
        },
    ];
    if (offerSource === OFFER_SOURCE.SHOPIFY) {
        columns.push({
            header: 'Promo Code',
            id: 'code',
            accessorKey: 'code',
            size: 170,
            minSize: 170,
            maxSize: 170,
            cell: ({ row }) => (
              <Tooltip>
                <TooltipTrigger>
                  <Text copyable ellipsis style={{ maxWidth: '100%', display: 'block' }}>
                    {row.original.code.length > 15 ? `${row.original.code.substring(0, 15)}...` : row.original.code}
                  </Text>
                </TooltipTrigger>
                <TooltipContent className="bg-grey-0 text-grey-6 shadow-lg border border-gray-200 rounded-md px-3 py-2 font-medium z-50 max-w-[400px]">
                  {row.original.code}
                </TooltipContent>
              </Tooltip>
            ),

        });
    } else if (offerSource === OFFER_SOURCE.TUNE) {
        columns.push({
            header: 'Affiliate Link',
            id: 'affiliateLink',
            accessorKey: 'affiliateLink',
            size: 170,
            minSize: 170,
            maxSize: 170,
        });
    }
    const refreshColumn: CustomColumn[] = [
        {
            header: 'Name',
            id: 'name',
            accessorKey: 'name',
            size: 170,
            minSize: 170,
            maxSize: 170,
            cell: ({ row }) => (
              <Tooltip>
                <TooltipTrigger>
                  {row.original.name.length > 20 ? `${row.original.name.substring(0, 20)}...` : row.original.name}
                </TooltipTrigger>
                <TooltipContent className="bg-grey-0 text-grey-6 shadow-lg border border-gray-200 rounded-md px-3 py-2 font-medium z-50 max-w-[400px]">
                  {row.original.name}
                </TooltipContent>
              </Tooltip>
            ),
        },
        {
            header: 'Previous Code',
            id: 'previousCode',
            accessorKey: 'previousCode',
            size: 270,
            minSize: 270,
            maxSize: 270,
            cell: ({ row }) => {
                return (
                  <Tooltip>
                    <TooltipTrigger>
                      <Text copyable ellipsis style={{ maxWidth: '100%', display: 'block' }}>
                        {row.original.previousCode.code}
                      </Text>
                    </TooltipTrigger>
                    <TooltipContent className="bg-grey-0 text-grey-6 shadow-lg border border-gray-200 rounded-md px-3 py-2 font-medium z-50 max-w-[400px]">
                      {row.original.previousCode.code}
                    </TooltipContent>
                  </Tooltip>
                );
            },
        },
        {
            header: 'New Promo Code',
            id: 'newCode',
            accessorKey: 'newCode',
            size: 170,
            minSize: 170,
            maxSize: 170,
            cell: ({ row }) => (
              <>
                <div className="flex items-center gap-2 w-full">
                  <Input
                    ref={(el) => {
                                inputRefs.current[row.original.id] = el;
                            }}
                    value={row.original.newCode || ''}
                    onChange={(e) => handleNewCodeChange(row.original.id, e)}
                    placeholder="TYPE CODE HERE"
                    className="focus:ring-2 focus:ring-purple-500"
                  />
                </div>
                {row.original.error && <span className="text-danger text-xs">Please enter code</span>}
                {row.original.isDuplicateError && <span className="text-danger text-xs">Promo code must be unique</span>}
              </>
            ),
        },
    ];
    const handleClearInput = useCallback(() => {
        const updatedData = data.map((record) => ({ ...record, newCode: '' }));
        setData(updatedData); // Assuming setData is a function to update the state
    }, [data]);
    const handleNewCodeChange = useCallback((id, e) => {
        const { value } = e.target;
        const updatedData = data.map((item) => {
            if (item.id === id) {
                return {
                    ...item,
                    newCode: cleanPromoCode(value),
                    error: value.trim() === '',
                };
            }
            return item;
        });

        const hasEmptyField = updatedData.some((item) => !item.newCode || item.newCode.trim() === '');
        const hasDuplicateNewCode = updatedData.some(
            (item) => item.newCode && updatedData.findIndex((elem) => elem.newCode === item.newCode && elem.id !== item.id) !== -1,
        );

        const newData = updatedData.map((item) => ({
            ...item,
            isDuplicateError: item.newCode && updatedData.filter((elem) => elem.newCode === item.newCode).length > 1,
        }));

        setData(newData);
        setIsDuplicateError(hasDuplicateNewCode);
        setIsError(hasEmptyField);

        // Maintain focus after state update
        requestAnimationFrame(() => {
            const input = inputRefs.current[id];
            if (input) {
                input.focus();
            }
        });
    }, [data]);
    const editPayoutData = transformMemberData(data, offerSource, offer);
    const activateDeactivatePayoutData = transformMemberData(memberToOperate, offerSource, offer);
    const handleConfirm = () => {
        save();
    };
    const handleActiveDatesChange = (fieldName: keyof ActiveDate, value: Date | boolean) => {
        setActiveDates((prevState) => ({
            ...prevState,
            [fieldName]: value,
        }));
    };
    const payoutOptions: { id: number | null; label: string }[] = useMemo(() => {
        const options: { id: number | null; label: string }[] = [];

        if (automatePayoutTier && isAutomatedPayoutEnabled) {
            // Group payouts by automation config
            const automatedPayouts = offer.payouts.filter((payout) => payout.offerAutomationConfigId);
            const regularPayouts = offer.payouts.filter((payout) => payout.offerAutomationConfigId === null);

            // Add automated tiers if they exist
            if (automatedPayouts.length > 0) {
                options.push({ id: AUTOMATED_PAYOUT_ID, label: AutomatedPayoutLabel });
            }

            // Add regular payouts
            regularPayouts.forEach((payout) => {
                const rules = payout.offerPayoutRules || [];
                if (rules.length > 0) {
                    options.push({ id: payout.id, label: `${payout.label} (Multiple)` });
                } else if (!isNull(payout.flatPayout) && !isNull(payout.percentPayout)) {
                    options.push({ id: payout.id, label: `${payout.label} (${payout.percentPayout}% + $${payout.flatPayout})` });
                } else if (!isNull(payout.flatPayout)) {
                    options.push({ id: payout.id, label: `${payout.label} ($${payout.flatPayout})` });
                } else if (!isNull(payout.percentPayout)) {
                    options.push({ id: payout.id, label: `${payout.label} (${payout.percentPayout}%)` });
                } else {
                    options.push({ id: payout.id, label: payout.label });
                }
            });
        } else {
            // Simple payout options without automation
            offer.payouts.forEach((payout) => {
                if (!isNull(payout.flatPayout) && !isNull(payout.percentPayout)) {
                    options.push({ id: payout.id, label: `${payout.label} (${payout.percentPayout}% + $${payout.flatPayout})` });
                } else if (!isNull(payout.flatPayout)) {
                    options.push({ id: payout.id, label: `${payout.label} ($${payout.flatPayout})` });
                } else if (!isNull(payout.percentPayout)) {
                    options.push({ id: payout.id, label: `${payout.label} (${payout.percentPayout}%)` });
                } else {
                    options.push({ id: payout.id, label: payout.label });
                }
            });
        }

        return options;
    }, [offer.payouts, automatePayoutTier, isAutomatedPayoutEnabled]);

    const handleClose = () => {
        makeEmptyStates();
        setMode(null);
        onClose();
    };

    const disableUpdateButtonRefreshDates = useMemo(() => {
        if (mode === OFFER_ACTION_TYPE.UPDATE_BULK_REFRESH_DATES || mode === OFFER_ACTION_TYPE.UPDATE_BULK_ACTIVE_DATES) {
            return !activeDates.startDate || (activeDates.showEndDate && !activeDates.endDate);
        }
        return false;
    }, [mode, activeDates]);
    return (
      <>
        <Sheet open={visible} onOpenChange={handleClose} modal={false}>
          <SheetContent side="right" className="flex flex-col h-full bg-grey-0 p-0 gap-0 w-[576px] !max-w-[576px]">
            <SheetHeader className="p-4 flex items-center gap-2 flex-row">
              <FontAwesomeIcon icon={faTags} className="text-purple-5 w-6 h-6" />
              <SheetTitle className="!m-0 text-base font-medium">Manage offer for members</SheetTitle>
            </SheetHeader>
            <div className="flex items-center gap-2 px-4 py-2 bg-grey-1 border-t border-b border-grey-2">
              <Button
                onClick={goBack}
                key={2}
                className="w-8 h-8"
                variant="outline"
                size="sm"
              >
                <FontAwesomeIcon icon={faArrowLeft} className="text-grey-6 w-4 h-4" />
              </Button>
              <OfferDetailCard
                showCta={false}
                isWorkFlow={isWorkFlow}
                offerData={offer}
                infoButtonClassName
              />
            </div>
            <div className="flex-1 overflow-y-auto p-4" style={{ maxHeight: 'calc(100vh - 160px)' }}>
              {mode === null && (
                <div className="mb-6">
                  <p className="m-0 text-base font-medium text-grey-6">Select the action you want to perform</p>
                  <p className="m-0 text-sm font-normal text-grey-4">Choose what you want to edit for the selected members in this offer.</p>
                </div>
                        )}
              {mode === null && (
                <div className="flex flex-col gap-2">
                  {isWorkFlow && selectedMembers.length < selectedMemberIds.length && (
                  <Alert
                    type="warning"
                    message={
                                            selectedMembers.length
                                                ? `From ${selectedMemberIds.length} member${selectedMemberIds.length > 1 ? 's' : ''}, only ${selectedMembers.length
                                                } member${selectedMembers.length > 1 ? 's are' : ' is'
                                                } associated with the connected offer. The remaining ${netMembers} member${netMembers > 1 ? 's are' : ' is'
                                                } not linked to this offer, and will therefore be excluded from the editing process.`
                                                : `No members are part of this connected offer, Please generate ${offer?.promos?.length ? 'promo code' : 'sales link'
                                                } to continue.`
                                        }
                  />
                                )}
                  <RefreshManageOfferItem
                    title="Update Payouts"
                    icon={<FontAwesomeIcon icon={faFileInvoiceDollar} className="text-teal-4 w-4 h-4" />}
                    onClick={() => handleSteps(OFFER_ACTION_TYPE.EDITPAYOUT)}
                    disabled={isNull(defaultPayout)}
                    errorMessage={
                                        isNull(defaultPayout)
                                        && 'This offer currently does not support multiple payouts. To enable editing, please upgrade the offer to support the multiple payouts feature.'
                                    }
                  />
                  {offerSource === OFFER_SOURCE.SHOPIFY && !first(offer.promos)?.isSecureCodes && (
                  <RefreshManageOfferItem
                    title="Refresh Promo Codes"
                    icon={<FontAwesomeIcon icon={faTag} className="text-green-4 w-6 h-6" />}
                    onClick={() => handleSteps(OFFER_ACTION_TYPE.REFRESH)}
                  />
                                )}
                  <RefreshManageOfferItem
                    title={offerSource === OFFER_SOURCE.SHOPIFY ? 'Activate/Deactivate Codes' : 'Activate/Deactivate Links'}
                    icon={<FontAwesomeIcon icon={faCirclePlay} className="text-yellow-4 w-6 h-6" />}
                    onClick={() => handleSteps(OFFER_ACTION_TYPE.DEACTIVATE)}
                  />
                  {offerSource === OFFER_SOURCE.SHOPIFY && isUngrouped && (
                  <RefreshManageOfferItem
                    title="Change Active Dates"
                    icon={<FontAwesomeIcon icon={faCalendar} className="text-blue-4 w-6 h-6" />}
                    onClick={() => handleSteps(OFFER_ACTION_TYPE.UPDATE_BULK_REFRESH_DATES)}
                  />
                                )}
                  {manageDeepLinksAllowed && offerSource === OFFER_SOURCE.TUNE && (
                  <RefreshManageOfferItem
                    title="Manage Deep Links"
                    icon={<FontAwesomeIcon icon={faLink} className="text-grey-6 w-6 h-6" />}
                    onClick={() => handleSteps(OFFER_ACTION_TYPE.DEEPLINK)}
                  />
                                )}
                </div>
                        )}
              {mode === OFFER_ACTION_TYPE.EDITPAYOUT && (
                <RefreshManageOfferPayout
                  defaultPayoutId={defaultPayoutId}
                  payoutOptions={payoutOptions}
                  setDefaultPayoutId={setDefaultPayoutId}
                  editPayoutData={editPayoutData}
                  selectedMembers={selectedMembers}
                  offer={offer}
                  offerSource={offerSource}
                  columns={columns}
                  data={data}
                />
                        )}
              {mode === OFFER_ACTION_TYPE.REFRESH && (
                <RefreshPromoCodes data={data} refreshColumn={refreshColumn} />
                        )}
              {(mode === OFFER_ACTION_TYPE.DEACTIVATE || mode === OFFER_ACTION_TYPE.ACTIVATE) && (
                <RefreshManageOfferActivation
                  onChange={onChange}
                  activeOrDeactive={activeOrDeactive}
                  memberToOperate={memberToOperate}
                  selectedMembers={selectedMembers}
                  activateDeactivatePayoutData={activateDeactivatePayoutData}
                  columns={columns}
                />
                        )}
              {(mode === OFFER_ACTION_TYPE.UPDATE_BULK_ACTIVE_DATES
                            || mode === OFFER_ACTION_TYPE.UPDATE_BULK_REFRESH_DATES) && (
                            <RefreshOfferBulkDateAction data={data} activeDates={activeDates} handleActiveDatesChange={handleActiveDatesChange} setIsError={setIsError} />
                            )}

              {mode === OFFER_ACTION_TYPE.DEEPLINK && (
                <ManageDeepLink
                  selectedActiveMembers={selectedActiveMembers}
                  setDefaultDeeplinkUrl={setDefaultDeeplinkUrl}
                  isFormViewVisible={isFormViewVisible}
                  setFormViewVisible={setFormViewVisible}
                  additionalDeeplinkGroupedMembers={additionalDeeplinkGroupedMembers}
                  formData={manageDeeplinkForm}
                  setFormData={setManageDeeplinkForm}
                  setLinkValidation={setLinkValidation}
                  selectedLabels={selectedLabels}
                  setSelectedLabels={setSelectedLabels}
                />
                        )}
            </div>
            <SheetFooter className="justify-start sm:justify-start p-4 absolute bottom-0 left-0 right-0 w-full border-t bg-grey-0 z-[99]">
              {mode !== null &&
                            (
                            <div className="flex gap-2 w-full">
                              {mode === OFFER_ACTION_TYPE.ACTIVATE || mode === OFFER_ACTION_TYPE.DEACTIVATE ? (
                                <div>
                                  <Popconfirm
                                    title={`Are you sure you want to ${mode === OFFER_ACTION_TYPE.ACTIVATE ? OFFER_ACTION_TYPE.ACTIVATE : OFFER_ACTION_TYPE.DEACTIVATE
                                                    } ${offerSource === OFFER_SOURCE.SHOPIFY ? 'promo code' : 'link'} for ${memberToOperate.length} ${memberToOperate.length === 1 ? 'member' : 'members'
                                                    }`}
                                    cancelText="Cancel"
                                    okText="Confirm"
                                    onConfirm={handleConfirm}
                                    disabled={isError}
                                    className="z-[9999]"
                                  >

                                    <Button disabled={isError} loading={isLoading}>
                                      Update Offer
                                    </Button>
                                  </Popconfirm>
                                </div>
                                    ) : mode === OFFER_ACTION_TYPE.REFRESH && isUngrouped ? (
                                      <div>
                                        <Button
                                          onClick={() => setMode(OFFER_ACTION_TYPE.UPDATE_BULK_ACTIVE_DATES)}
                                          disabled={isError || isDuplicateError || selectedMembers.length == 0}
                                        >
                                          Set Active Dates
                                        </Button>
                                      </div>
                                    ) : mode === OFFER_ACTION_TYPE.DEEPLINK ? (
                                        isFormViewVisible ? (
                                          <div>
                                            <Popconfirm
                                              title={`Are you sure you want to update Primary Link for ${selectedOfferMember.filter(
                                                        (selectedMember) => selectedMember.status.toLowerCase() === AFFILIATE_STATUS.ACTIVE,
                                                    ).length
                                                        } ${selectedOfferMember.filter(
                                                            (selectedMember) => selectedMember.status.toLowerCase() === AFFILIATE_STATUS.ACTIVE,
                                                        ).length === 1
                                                            ? 'member'
                                                            : 'members'
                                                        }`}
                                              cancelText="Cancel"
                                              okText="Confirm"
                                              onConfirm={handleConfirm}
                                              disabled={linkValidation}
                                              className="z-[9999]"
                                            >
                                              <Tooltip>
                                                <TooltipTrigger>
                                                  <Button
                                                    disabled={linkValidation || isError || isDuplicateError || selectedActiveMembers.length === 0}
                                                    loading={isLoading}
                                                  >
                                                    Update
                                                  </Button>
                                                </TooltipTrigger>
                                                <TooltipContent className="bg-grey-0 text-grey-6 shadow-lg border border-gray-200 rounded-md px-3 py-2 font-medium z-50 max-w-[400px]">
                                                  {linkValidation && 'Either URL or Label is empty.'}
                                                </TooltipContent>
                                              </Tooltip>
                                            </Popconfirm>
                                          </div>
                                        ) : (
                                          <>
                                            <div>
                                              <Popconfirm
                                                title="Are you sure you want to remove selected deeplinks?"
                                                cancelText="No"
                                                okText="Confirm"
                                                onConfirm={handleRemoveDeeplinks}
                                                disabled={removeDisabled}
                                                className="z-[9999]"
                                              >
                                                {!showDeeplinkRemoveTooltip ? (
                                                  <Button disabled={removeDisabled}>
                                                    Remove
                                                  </Button>
                                                        ) : (
                                                          <Tooltip>
                                                            <TooltipTrigger>
                                                              <Button disabled={removeDisabled}>
                                                                Remove
                                                              </Button>
                                                            </TooltipTrigger>
                                                            <TooltipContent className="bg-grey-0 text-grey-6 shadow-lg border border-gray-200 rounded-md px-3 py-2 font-medium z-50 max-w-[400px]">
                                                              You cannot remove Primary Link. Please uncheck Primary Link to continue.
                                                            </TooltipContent>
                                                          </Tooltip>
                                                        )}
                                              </Popconfirm>
                                            </div>
                                            <div>
                                              <Button
                                                onClick={() => setFormViewVisible(true)}
                                                disabled={
                                                            isError
                                                            || isDuplicateError
                                                            || selectedActiveMembers.length === 0
                                                            || selectedLabels.length === 0
                                                        }
                                              >
                                                Update
                                                {' '}
                                                {selectedActiveMembers.length === 1 ? 'Link' : 'Links'}
                                                {' '}
                                                <ChevronRightIcon />
                                              </Button>
                                            </div>
                                          </>
                                        )
                                    ) : (
                                      <div>
                                        <Tooltip>
                                          <TooltipTrigger>
                                            <Button
                                              onClick={save}
                                              disabled={
                                                            isError || isDuplicateError || selectedMembers.length == 0 || disableUpdateButtonRefreshDates
                                                        }
                                              loading={isLoading}
                                            >
                                              Update Offer
                                            </Button>
                                          </TooltipTrigger>
                                          <TooltipContent className="bg-grey-0 text-grey-6 shadow-lg border border-gray-200 rounded-md px-3 py-2 font-medium z-50 max-w-[400px]">
                                            {isError && 'Please enter the code for all the members'}
                                            {isDuplicateError && 'Please enter unique Promo code for all the members'}
                                          </TooltipContent>

                                        </Tooltip>
                                      </div>
                                    )}
                              <div>
                                <Button onClick={goBack} variant="outline">
                                  Cancel
                                </Button>
                              </div>
                            </div>
                            )}
            </SheetFooter>
          </SheetContent>
        </Sheet>
      </>
    );
});

//
